/**
 * CSV Export Utilities
 *
 * Used by:
 * - ExportToCsvButton (src/main/ui/reports/ExportToCsvButton.js)
 * - ExportToCsvDropdownButton (src/main/ui/reports/ExportToCsvDropdownButton.js)
 */

import { unparse } from 'papaparse';

/**
 * Exports data to CSV format and triggers download
 * @param {Array} data - The data to export
 * @param {Array} columns - Column definitions with dataIndex, title, and optional render functions
 * @param {string} fileName - The base filename for the export (e.g., 'export.csv')
 * @returns {Promise<void>}
 */
export const exportToCsv = async (data, columns, fileName = 'export.csv') => {
    if (!data || data.length === 0) {
        console.warn('No data to export');
        return;
    }

    const exportableColumns = columns.filter(col => col.dataIndex);
    const headers = exportableColumns.map(col => col.title);

    const csvData = data.map(record => {
        return exportableColumns.map(col => {
            const value = record[col.dataIndex];

            if (col.render) {
                try {
                    const renderedValue = col.render(value, record);
                    if (typeof renderedValue === 'object' && renderedValue !== null) {
                        return value;
                    }
                    if (renderedValue === 'Invalid Date') {
                        return value;
                    }
                    return renderedValue;
                } catch (error) {
                    return value;
                }
            }

            return value;
        });
    });

    const csvString = unparse({
        fields: headers,
        data: csvData,
    });

    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });

    const now = new Date();
    const timestamp =
        (now.getMonth() + 1).toString().padStart(2, '0') +
        now.getDate().toString().padStart(2, '0') +
        now.getHours().toString().padStart(2, '0') +
        now.getMinutes().toString().padStart(2, '0') +
        now.getSeconds().toString().padStart(2, '0');

    const nameParts = fileName.split('.');
    const baseName = nameParts.slice(0, -1).join('.');
    const extension = nameParts.length > 1 ? `.${nameParts.pop()}` : '';
    const finalFileName = `${baseName}_${timestamp}${extension}`;

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', finalFileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
